
import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { AdService } from '../AdService';

interface UseClueModalProps {
    isOpen: boolean;
    onClose: () => void;
    onUseClue: () => void;
    onPurchaseClues: () => void;
    onWatchAd: (onComplete: () => void) => void;
    clueCount: number;
    triggerRect: DOMRect | null;
}

const UseClueModal: React.FC<UseClueModalProps> = ({ isOpen, onClose, onUseClue, onPurchaseClues, onWatchAd, clueCount }) => {
    const [isAdLoading, setIsAdLoading] = useState(false);
    
    if (!isOpen) return null;

    const handleWatchAdClick = () => {
        setIsAdLoading(true);
        onWatchAd(() => {
            // The onComplete logic (adding clue, toast) is handled in PostCard.
            // This just resets the loading state here.
            setIsAdLoading(false);
        });
    };

    return createPortal(
        <div 
            className="fixed inset-0 bg-black/70 z-[9999] flex items-center justify-center p-4 animate-fade-in" 
            onClick={onClose}
            role="dialog"
            aria-modal="true"
            aria-labelledby="hint-modal-title"
        >
            <div 
                className="bg-gradient-to-br from-purple-800 to-indigo-900 rounded-2xl shadow-2xl w-full max-w-sm text-center text-white border-2 border-purple-500/50 animate-modal-pop-in" 
                onClick={e => e.stopPropagation()}
            >
                <div className="p-6">
                    <i className="ph-duotone ph-lightbulb text-6xl aurora-text mb-3 animate-pulse"></i>
                    <h2 id="hint-modal-title" className="text-3xl font-bold mb-2 aurora-text">Need a hint?</h2>
                    <p className="text-gray-300 mb-6">You have <span className="font-bold text-yellow-300">{clueCount}</span> clue{clueCount !== 1 ? 's' : ''} left.</p>

                    <div className="space-y-3">
                         <button
                            onClick={onUseClue}
                            disabled={clueCount <= 0}
                            className="w-full bg-gradient-to-r from-pink-500 to-yellow-500 text-white font-bold py-3 px-4 rounded-lg shadow-md disabled:bg-gray-500 disabled:opacity-60 transition-all hover:opacity-90"
                        >
                            Use 1 Clue
                        </button>
                        <button
                            onClick={handleWatchAdClick}
                            disabled={isAdLoading}
                            className="w-full bg-emerald-500 text-white font-bold py-3 px-4 rounded-lg shadow-md flex items-center justify-center disabled:opacity-70 disabled:cursor-wait transition-all hover:opacity-90"
                        >
                            {isAdLoading ? (
                                <>
                                    <div className="spinner-white h-5 w-5 mr-2"></div>
                                    Loading Ad...
                                </>
                            ) : (
                                "Watch Ad for a FREE Clue"
                            )}
                        </button>
                         <button
                            onClick={onPurchaseClues}
                            className="w-full bg-transparent border border-white/50 text-white font-bold py-3 px-4 rounded-lg shadow-md transition-all hover:bg-white/10"
                        >
                            Buy More Clues
                        </button>
                         <button
                            onClick={onClose}
                            className="w-full text-gray-400 font-bold py-2 px-4 rounded-lg mt-2 hover:text-white transition-colors"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default UseClueModal;
